import Axhelper from '@/lib/Axhelper'

class PowiatService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG.API_URL
		this.endpoints = {
			powiatAll: '/api/powiat/filters',
			powiatDetails: '/api/powiat/details',
			powiatSearch: '/api/powiat/filters',
			powiatCreate: '/api/powiat',
			powiatUpdate: '/api/powiat',
			powiatDelete: '/api/powiat',
		}
	}

	async getAll(options = {}) {
		try {
			const {
				limit = 50,
				sortColumn = '_id',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = new URLSearchParams({
				sortcol: sortColumn,
				sortdir: sortDirection,
				limit: String(limit),
			})
			if (filters?.id_wojewodztwo) {
				params.append('id_wojewodztwo', String(filters.id_wojewodztwo))
			}
			const url = `${this.endpoints.powiatAll}?${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0

			return {
				data: response.data || [],
				error: null,
				totalCount,
				hasMore: (response.data?.length || 0) < totalCount,
				headers: response.headers,
			}
		} catch (error) {
			console.error('PowiatService.getAll error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch powiaty',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async getById(powiatId) {
		try {
			if (!powiatId) {
				return {
					data: null,
					error: 'Invalid powiat ID provided',
				}
			}

			const url = `${this.endpoints.powiatDetails}/${encodeURIComponent(powiatId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: null,
					error: response.error,
				}
			}

			return {
				data: response.data || null,
				error: null,
			}
		} catch (error) {
			console.error('PowiatService.getById error:', error)
			return {
				data: null,
				error: error.message || 'Failed to fetch powiat details',
			}
		}
	}

	async search(searchTerm, options = {}) {
		try {
			if (!searchTerm) {
				return {
					data: [],
					error: 'Invalid search term provided',
					totalCount: 0,
					hasMore: false,
				}
			}

			const {
				limit = 50,
				sortColumn = '_id',
				sortDirection = 'desc',
				filters = {},
			} = options

			const params = new URLSearchParams({
				sortcol: sortColumn,
				sortdir: sortDirection,
				limit: String(limit),
			})
			if (filters?.id_wojewodztwo) {
				params.append('id_wojewodztwo', String(filters.id_wojewodztwo))
			}
			const url = `${this.endpoints.powiatSearch}?searchText=${encodeURIComponent(searchTerm)}&${params.toString()}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0

			return {
				data: response.data || [],
				error: null,
				totalCount,
				hasMore: (response.data?.length || 0) < totalCount,
				headers: response.headers,
			}
		} catch (error) {
			console.error('PowiatService.search error:', error)
			return {
				data: [],
				error: error.message || 'Failed to search powiaty',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	async create(powiatData) {
		try {
			const response = await Axhelper.sendPostAsync(this.endpoints.powiatCreate, powiatData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('PowiatService.create error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create powiat',
			}
		}
	}

	async update(powiatId, powiatData) {
		try {
			if (!powiatId) {
				return {
					success: false,
					error: 'Invalid powiat ID provided',
				}
			}

			const url = `${this.endpoints.powiatUpdate}/${encodeURIComponent(powiatId)}`
			const response = await Axhelper.sendPutAsync(url, powiatData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('PowiatService.update error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update powiat',
			}
		}
	}

	async delete(powiatId) {
		try {
			if (!powiatId) {
				return {
					success: false,
					error: 'Invalid powiat ID provided',
					message: null,
				}
			}

			const url = `${this.endpoints.powiatDelete}/${encodeURIComponent(powiatId)}`
			const response = await Axhelper.sendDeleteAsync(url)

			if (response.error) {
				return {
					success: false,
					error: response.error,
					message: null,
				}
			}

			return {
				success: true,
				error: null,
				message: response.data || 'Powiat deleted successfully',
			}
		} catch (error) {
			console.error('PowiatService.delete error:', error)
			return {
				success: false,
				error: error.message || 'Failed to delete powiat',
				message: null,
			}
		}
	}
}

export default new PowiatService()
